import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type { WricefDecisionItem } from "@/api/wricef-decisions";
import { DecisionForm } from "@/components/agents/WricefDecisionSection";
import { useCurrentUser } from "@/hooks/useAuth";
import { useWricefDecisionList } from "@/hooks/useWricefDecisions";
import { formatCount } from "@/lib/format";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

import { DECISION_BAR } from "../acts/environment/WricefCard";
import { DECISION_ORDER, UNDECIDED, type DecisionOrUndecided } from "../data/wricefDecisions";
import type { DiagnosticTabProps } from "../DiagnosticSection";
import { makeDecisionMeta, makeWricefTypeMeta } from "../diagnosticMeta";
import { FilterButton } from "../orgStructure/FilterButton";
import { NO_VALUE } from "../sectionState";

/** A decisão de uma linha, com o balde "sem decisão" no lugar do `null` da API. */
function decisionOf(item: WricefDecisionItem): DecisionOrUndecided {
  return item.decision ?? UNDECIDED;
}

/** O que o objeto é: a descrição funcional ajustada, a sugerida ou o comportamento lido. */
function whatItIs(item: WricefDecisionItem): string | null {
  return item.functional_description ?? item.functional_description_suggested ?? item.current_behavior ?? null;
}

/**
 * O drill-down do card "Código customizado por tipo (WRICEF)" do Ato 1 (#1477,
 * RF-08 da #1343).
 *
 * É a lista que vai para a reunião com o cliente: cada objeto entregável com a
 * decisão do arquiteto e o critério que a justifica, filtrável por decisão. A
 * URL guarda o filtro (`?decision=`), e é por ela que a legenda do card abre a
 * lista já filtrada.
 *
 * Quem tem papel de consultor troca a decisão na própria linha, pelo `PUT` da
 * F1 com origem `diagnostic`: os pendentes são decididos ali, com o cliente. O
 * papel `user` vê a lista sem o botão. Sem valor financeiro, como a lista da F1.
 */
export function WricefDetail(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, refetch } = useWricefDecisionList(projectId);
  const canEdit = hasMinRole(useCurrentUser()?.role, "consultant");
  const [editing, setEditing] = useState<string | null>(null);

  const meta = makeDecisionMeta(t);
  const typeMeta = makeWricefTypeMeta(t);
  const filter = params.get("decision") as DecisionOrUndecided | null;
  const items = useMemo(() => data?.items ?? [], [data]);

  const counts = useMemo(() => {
    const byDecision: Record<string, number> = {};
    for (const entry of items) {
      const token = decisionOf(entry);
      byDecision[token] = (byDecision[token] ?? 0) + 1;
    }
    return byDecision;
  }, [items]);

  const shown = useMemo(
    () => (filter ? items.filter((entry) => decisionOf(entry) === filter) : items),
    [items, filter],
  );

  function choose(token: DecisionOrUndecided | null) {
    setEditing(null);
    setParams(token ? { decision: token } : {}, { replace: true });
  }

  if (isLoading) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }
  if (!data) {
    // Falha não é lista vazia: "nenhum objeto" afirmaria algo sobre o projeto.
    return (
      <div
        data-testid="wricef-detail-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the WRICEF objects.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#e3e8ef] bg-white">
      <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] p-3">
        <FilterButton testId="wricef-filter-all" active={filter === null} onClick={() => choose(null)}>
          {t("All")} {formatCount(items.length)}
        </FilterButton>
        {DECISION_ORDER.map((token) => (
          <FilterButton
            key={token}
            testId={`wricef-filter-${token}`}
            active={filter === token}
            onClick={() => choose(token)}
          >
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-sm", DECISION_BAR[token])} />
              {meta[token].label} {formatCount(counts[token] ?? 0)}
            </span>
          </FilterButton>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table data-testid="wricef-detail-table" className="w-full min-w-[960px] border-collapse text-left">
          <thead>
            <tr className="border-b border-[#eef1f5] font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
              <th className="px-3 py-2 font-medium">{t("Object")}</th>
              <th className="px-3 py-2 font-medium">{t("Type")}</th>
              <th className="px-3 py-2 font-medium">{t("Description")}</th>
              <th className="px-3 py-2 font-medium">{t("Decision")}</th>
              <th className="px-3 py-2 font-medium">{t("Criterion")}</th>
              {canEdit && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {shown.map((entry) => {
              const token = decisionOf(entry);
              const letter = entry.wricef ?? "";
              const isEditing = editing === entry.obj_name;
              return (
                <Fragment key={entry.obj_name}>
                  <tr
                    data-testid={`wricef-item-${entry.obj_name}`}
                    className="border-b border-[#eef1f5] align-top text-[12px] text-[#3c4b61]"
                  >
                    <td className="px-3 py-2">
                      <span className="font-mono font-semibold text-[#08254c]">{entry.obj_name}</span>
                      {entry.obj_type_tadir && (
                        <span className="text-[#7b8799]"> · {entry.obj_type_tadir}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{typeMeta[letter]?.label ?? (letter || NO_VALUE)}</td>
                    <td className="max-w-[320px] whitespace-pre-line px-3 py-2">{whatItIs(entry) ?? NO_VALUE}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 font-medium text-[#08254c]">
                        <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-sm", DECISION_BAR[token])} />
                        {meta[token]?.label ?? token}
                      </span>
                      {entry.decision === "covered_by_standard" && entry.standard_scope_item && (
                        <span className="font-mono text-[#596b85]"> · {entry.standard_scope_item}</span>
                      )}
                    </td>
                    <td className="max-w-[420px] whitespace-pre-line px-3 py-2">{entry.note || NO_VALUE}</td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          data-testid="wricef-item-edit"
                          aria-expanded={isEditing}
                          onClick={() => setEditing(isEditing ? null : entry.obj_name)}
                          className="whitespace-nowrap rounded-md border border-[#d4dae3] px-2.5 py-1 text-[11px] font-semibold text-[#08254c] hover:border-[#08254c]"
                        >
                          {isEditing ? t("Cancel") : t("Change decision")}
                        </button>
                      </td>
                    )}
                  </tr>
                  {isEditing && projectId && (
                    <tr className="border-b border-[#eef1f5] bg-[#f8fafc]">
                      <td colSpan={6} className="px-4 py-4" data-testid={`wricef-item-editor-${entry.obj_name}`}>
                        <DecisionForm
                          key={entry.version ?? 0}
                          projectId={projectId}
                          item={entry}
                          decidedIn="diagnostic"
                          onSaved={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[12px] text-[#7b8799]">
                  {t("No items for the selected filters.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
